// Frontend/src/pages/auth/AdminLoginPage.tsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import logoImg from "@/assets/logo.png";
import { useLanguage } from "@/contexts/LanguageContext";

const PRIMARY = "#1F3A93";
const ACCENT = "#27AE60";
const BG = "#F5F6FA";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useLanguage();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // If we already have a session, redirect based on role
    // BUT: Residents use localStorage tokens, not sessionStorage, so ignore RESIDENT role
    if (user && user.role !== "RESIDENT") {
      navigate(roleHome(user.role), { replace: true });
    }
  }, [user, navigate]);

  function hexToRgba(hex: string, alpha = 1) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  function roleHome(role?: string | null) {
    switch (role) {
      case "SUPER_ADMIN":
        return "/super-admin";
      case "HOA_ADMIN":
        return "/hoa-admin";
      case "RESIDENT":
      default:
        return "/resident";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const u = await login(email.trim(), password);
      navigate(roleHome(u.role), { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        t("auth.signInError");
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={logoImg}
            alt="Urbe360 logo"
            width={124}
            height={124}
            className="mb-2"
          />
          <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <h1 className="text-center text-3xl font-semibold mb-2 text-gray-900">
          {t("auth.adminAccessTitle")}
        </h1>
        <p className="text-center text-sm text-gray-600 mb-8">
          {t("auth.superAdminAccess")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm text-gray-800">
              {t("auth.emailOrId")}
            </label>
            <input
              id="email"
              type="text"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder={t("auth.adminEmailPlaceholder")}
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PRIMARY;
                e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(ACCENT, 0.14)}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm text-gray-800">
              {t("auth.password")}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="••••••••"
                className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 pr-12 outline-none transition"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = PRIMARY;
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(ACCENT, 0.14)}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="group inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all active:scale-[0.99] disabled:opacity-60"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
          >
            {isLoading ? t("auth.signingIn") : t("auth.signInAdmin")}
            <ArrowRightCircleIcon className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
          </button>
        </form>

        {/* Back to regular login */}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
            {t("auth.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}

/* Inline icons to avoid extra deps */
function EyeIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function EyeSlashIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M2 12s3.5-7 10-7c2.4 0 4.4.8 6 2M22 12s-3.5 7-10 7c-2.4 0-4.4-.8-6-2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" opacity="0.85" />
      <path d="M10 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
